import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, throwError, of, Subject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { authCodeFlowConfig } from '../../app.constants';
import { environment } from '../../../environments/environment';
import { catchError, tap, finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _isLoggingOut = false;
  private authReadySource = new Subject<void>(); // Use Subject to broadcast when auth is ready
  authReady$ = this.authReadySource.asObservable();

  constructor(
    private oauthService: OAuthService,
    private router: Router,
    private http: HttpClient
  ) {}

  async initAuth(): Promise<void> {
    try {
      this.oauthService.configure(authCodeFlowConfig);
      await this.oauthService.loadDiscoveryDocumentAndLogin();
      this.oauthService.setupAutomaticSilentRefresh();
      this.authReadySource.next(); // Signal that auth is ready after successful init
      this.authReadySource.complete(); //complete the stream
    } catch (error) {
      console.error('Error during authentication initialization:', error);
      throw error;
    }
  }

  checkAndRefreshToken(): Observable<any> {
    if (!this.isTokenExpired()) {
      return of(null);
    }

    return this.refreshToken().pipe(
      tap((response) => {
        this.storeTokens(response);
      }),
      catchError((err) => {
        console.error('Error updating token:', err);
        this.handleRefreshTokenError(err);
        return throwError(() => err);
      })
    );
  }

  refreshToken(): Observable<any> {
    const refreshToken = sessionStorage.getItem('refresh_token');
    if (!refreshToken) {
      return throwError(() => new Error('Refresh token not found'));
    }

    const body = this.createRefreshTokenBody(refreshToken);
    const headers = this.createHeaders();

    return this.http.post(environment.urlkeycloackrefresh, body, { headers }).pipe(
      tap((response: any) => {
        this.storeTokens(response);
      }),
      catchError((error) => {
        this.handleRefreshTokenError(error);
        return throwError(() => error);
      })
    );
  }

  private createRefreshTokenBody(refreshToken: string): string {
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refreshToken);
    body.set('client_id', environment.clientId);
    return body.toString();
  }

  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
  }

  private storeTokens(response: any): void {
    sessionStorage.setItem('access_token', response.access_token);
    sessionStorage.setItem('refresh_token', response.refresh_token);
    //get exp from oauthService
    const token = this.oauthService.getAccessToken();
    if (token) {
      const payload = token.split('.')[1];
      const payloadDecoded = JSON.parse(atob(payload));
      const expiresAt = payloadDecoded.exp * 1000;
      sessionStorage.setItem('expires_at', expiresAt.toString());
    }
  }

  private handleRefreshTokenError(error: any): void {
    console.error('Error while renewing token:', error);
    this.logout();
  }

  async getAccessToken(): Promise<string | null> {
    try {
      await this.checkAndRefreshToken().toPromise();
    } catch (err) {
      console.error('Error while checking and refreshing token on getAccessToken:', err);
    }
    return sessionStorage.getItem('access_token');
  }

  getUfUsuario(): string {
    const tokenDecoded = this.decodeToken();
    if (!tokenDecoded || !tokenDecoded.groups) {
      return 'br';
    }
    for (const group of tokenDecoded.groups) {
      const match = group.match(/\/cfm\/(.*?)\//);
      if (match && match[1]) {
        return match[1];
      }
    }
    return 'br';
  }

  async login(): Promise<boolean> {
    try {
      await this.initAuth();
      return this.isAuthenticated().toPromise();
    } catch (error) {
      console.error('Error during login:', error);
      return false;
    }
  }

  get isLoggingOut(): boolean {
    return this._isLoggingOut;
  }

  async logout(): Promise<void> {
    if (this._isLoggingOut) {
      return;
    }

    try {
      this._isLoggingOut = true;
      this.clearSessionTokens();
      await this.oauthService.logOut();
      await new Promise((resolve) => setTimeout(resolve, 300));
      await this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setTimeout(() => {
        this._isLoggingOut = false;
      }, 500);
    }
  }

  isAuthenticated(): Observable<boolean> {
    return of(this.oauthService.hasValidAccessToken());
  }

  getExpiresTime(): number {
    //removed logic, since this value is not used anymore.
    return 0;
  }

  getExpiresAt(): string | null {
    const expiresAt = sessionStorage.getItem('expires_at');
    if (expiresAt) {
      if (isNaN(parseInt(expiresAt))) {
        console.error('expires_at inválido no sessionStorage.');
        return null;
      }
    }
    return expiresAt;
  }

  getUsuario(): any | null {
    return this.oauthService.getIdentityClaims() || null;
  }

  isFuncionario(): boolean {
    const tokenDecoded = this.decodeToken();
    if (!tokenDecoded || !tokenDecoded.realm_access || !tokenDecoded.realm_access.roles) {
      return false;
    }
    return tokenDecoded.realm_access.roles.includes('colaborador');
  }

  isAdmin(): boolean {
    const tokenDecoded = this.decodeToken();
    if (
      !tokenDecoded ||
      !tokenDecoded.resource_access ||
      !tokenDecoded.resource_access['painel-financeiro-api'] ||
      !tokenDecoded.resource_access['painel-financeiro-api'].roles
    ) {
      return false;
    }
    return tokenDecoded.resource_access['painel-financeiro-api'].roles.includes('admin');
  }

  decodeToken(): any | null {
    const token = this.oauthService.getAccessToken();
    if (!token) return null;

    try {
      const payload = token.split('.')[1];
      const payloadDecoded = atob(payload);
      return JSON.parse(payloadDecoded);
    } catch (error) {
      console.error('Error decoding the token:', error);
      return null;
    }
  }

  async redirectUserBasedOnRole(): Promise<void> {
    const isFuncionario = this.isFuncionario();
    if (isFuncionario) {
      await this.router.navigate(['/']);
    } else {
      await this.router.navigate(['/acesso-negado']);
    }
  }

  private clearSessionTokens(): void {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('expires_at');
  }

  private isTokenExpired(): boolean {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) {
      return true;
    }
    return parseInt(expiresAt, 10) < Date.now();
  }
}
